import { forwardRef, useMemo } from "react";

export type ChartKind = "funnel" | "bar" | "line" | "donut";

interface ChartDatum {
  label: string;
  value: number;
}

interface Props {
  kind: ChartKind;
  title: string;
  groupLabel: string;
  metricLabel: string;
  data: ChartDatum[];
}

const colors = ["#0B6FEA", "#3C8CF0", "#62A5F4", "#80B9F7", "#9CCAF9", "#B7D9FB", "#D1E8FC", "#E2F1FD"];
const opportunityStageOrder = [
  "Prospecting", "初步接洽", "Qualification", "资格确认", "Needs Analysis", "需求分析",
  "Value Proposition", "价值建议", "Proposal/Price Quote", "方案/报价",
  "Negotiation/Review", "谈判/评审", "Closed Won", "赢单", "Closed Lost", "丢单"
];

function sortedData(kind: ChartKind, input: ChartDatum[]): ChartDatum[] {
  if (kind !== "funnel") return input.slice(0, 20);
  const order = new Map(opportunityStageOrder.map((name, index) => [name, index]));
  return [...input].sort((left, right) =>
    (order.get(left.label) ?? 999) - (order.get(right.label) ?? 999)
  ).slice(0, 20);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2 }).format(value);
}

export const ResultChart = forwardRef<SVGSVGElement, Props>(function ResultChart({ kind, title, groupLabel, metricLabel, data: input }, ref) {
  const data = useMemo(() => sortedData(kind, input), [input, kind]);
  const max = Math.max(1, ...data.map(({ value }) => value));
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (kind === "funnel") {
    const height = Math.max(230, data.length * 54 + 70);
    return <svg ref={ref} className="result-chart" viewBox={`0 0 720 ${height}`} role="img" aria-label={`${title}漏斗图`}>
      <title>{title}：按{groupLabel}展示{metricLabel}</title>
      <rect width="720" height={height} fill="#fff" />
      <text x="24" y="31" fill="#17324d" fontSize="18" fontWeight="700">{title}</text>
      <text x="696" y="31" textAnchor="end" fill="#64788c" fontSize="12">{metricLabel}</text>
      {data.map((item, index) => {
        const y = 50 + index * 54;
        const width = 210 + 360 * (item.value / max);
        const left = (720 - width) / 2;
        const inset = Math.min(22, width * 0.08);
        return <g key={`${item.label}:${index}`}>
          <polygon points={`${left},${y} ${left + width},${y} ${left + width - inset},${y + 44} ${left + inset},${y + 44}`} fill={colors[index % colors.length]} />
          <text x="360" y={y + 20} textAnchor="middle" fill={index < 4 ? "#fff" : "#17324d"} fontSize="13" fontWeight="700">{item.label || "未设置"}</text>
          <text x="360" y={y + 37} textAnchor="middle" fill={index < 4 ? "#eaf4ff" : "#31506f"} fontSize="11">{formatNumber(item.value)}</text>
        </g>;
      })}
    </svg>;
  }

  if (kind === "bar") {
    const height = Math.max(230, data.length * 46 + 70);
    return <svg ref={ref} className="result-chart" viewBox={`0 0 720 ${height}`} role="img" aria-label={`${title}柱状图`}>
      <title>{title}：按{groupLabel}比较{metricLabel}</title>
      <rect width="720" height={height} fill="#fff" />
      <text x="24" y="31" fill="#17324d" fontSize="18" fontWeight="700">{title}</text>
      {data.map((item, index) => {
        const y = 54 + index * 46;
        const width = 430 * (item.value / max);
        return <g key={`${item.label}:${index}`}>
          <text x="190" y={y + 17} textAnchor="end" fill="#526980" fontSize="12">{item.label || "未设置"}</text>
          <rect x="205" y={y} width={Math.max(2, width)} height="24" rx="5" fill={colors[index % colors.length]} />
          <text x={Math.min(680, 218 + width)} y={y + 17} fill="#17324d" fontSize="12" fontWeight="700">{formatNumber(item.value)}</text>
        </g>;
      })}
    </svg>;
  }

  if (kind === "line") {
    const points = data.map((item, index) => ({
      ...item,
      x: data.length <= 1 ? 360 : 70 + index * (580 / (data.length - 1)),
      y: 260 - (item.value / max) * 170
    }));
    return <svg ref={ref} className="result-chart" viewBox="0 0 720 330" role="img" aria-label={`${title}趋势图`}>
      <title>{title}：{metricLabel}趋势</title>
      <rect width="720" height="330" fill="#fff" />
      <text x="24" y="31" fill="#17324d" fontSize="18" fontWeight="700">{title}</text>
      <line x1="60" y1="270" x2="670" y2="270" stroke="#cbd9e6" />
      <polyline points={points.map(({ x, y }) => `${x},${y}`).join(" ")} fill="none" stroke="#0B6FEA" strokeWidth="4" strokeLinejoin="round" />
      {points.map((item, index) => <g key={`${item.label}:${index}`}>
        <circle cx={item.x} cy={item.y} r="6" fill="#0B6FEA" />
        <text x={item.x} y={item.y - 12} textAnchor="middle" fill="#17324d" fontSize="11" fontWeight="700">{formatNumber(item.value)}</text>
        <text x={item.x} y="292" textAnchor="middle" fill="#65798d" fontSize="10">{item.label.slice(0, 12)}</text>
      </g>)}
    </svg>;
  }

  let cumulative = 0;
  return <svg ref={ref} className="result-chart" viewBox="0 0 720 340" role="img" aria-label={`${title}占比图`}>
    <title>{title}：{metricLabel}占比</title>
    <rect width="720" height="340" fill="#fff" />
    <text x="24" y="31" fill="#17324d" fontSize="18" fontWeight="700">{title}</text>
    <g transform="rotate(-90 210 185)">
      <circle cx="210" cy="185" r="92" fill="none" stroke="#edf3f8" strokeWidth="46" />
      {data.map((item, index) => {
        const percent = total ? item.value / total * 100 : 0;
        const offset = -cumulative;
        cumulative += percent;
        return <circle key={`${item.label}:${index}`} cx="210" cy="185" r="92" pathLength="100" fill="none" stroke={colors[index % colors.length]} strokeWidth="46" strokeDasharray={`${percent} ${100 - percent}`} strokeDashoffset={offset} />;
      })}
    </g>
    <text x="210" y="180" textAnchor="middle" fill="#66798b" fontSize="12">总计</text>
    <text x="210" y="207" textAnchor="middle" fill="#17324d" fontSize="24" fontWeight="700">{formatNumber(total)}</text>
    {data.slice(0, 8).map((item, index) => <g key={`${item.label}:legend`} transform={`translate(390 ${82 + index * 30})`}>
      <rect width="12" height="12" rx="3" fill={colors[index % colors.length]} />
      <text x="20" y="11" fill="#526980" fontSize="11">{item.label || "未设置"}</text>
      <text x="292" y="11" textAnchor="end" fill="#17324d" fontSize="11" fontWeight="700">{total ? `${(item.value / total * 100).toFixed(1)}%` : "0%"}</text>
    </g>)}
  </svg>;
});
